import type { AllocationTarget } from '../admin/AllocationTargetField';

/**
 * The intake fields shared by the one-off "add person" page and step 1 of
 * the guided new-employee wizard. Kept separate from the JSX so the
 * validity rule and the request-body shape have one definition and can be
 * unit tested without rendering.
 */
export interface PersonDetailsValue {
  firstName: string;
  lastName: string;
  email: string;
  personalEmail: string;
  employeeId: string;
  roleId: string;
  manager: string;
  contract: string;
  startDate: string;
  notes: string;
  /** Existing staff attending only to collect kit; skips onboarding content. */
  onboardingHidden: boolean;
}

export const emptyPersonDetails: PersonDetailsValue = {
  firstName: '',
  lastName: '',
  email: '',
  personalEmail: '',
  employeeId: '',
  roleId: '',
  manager: '',
  contract: '',
  startDate: '',
  notes: '',
  onboardingHidden: false,
};

/** Minimum to create a person: both names plus something email-shaped. */
export function personDetailsValid(v: PersonDetailsValue): boolean {
  return Boolean(
    v.firstName.trim() && v.lastName.trim() && /.+@.+\..+/.test(v.email.trim()),
  );
}

/**
 * Build the POST /api/admin/people body. Optional fields are omitted rather
 * than sent blank, because the API treats an absent key as "not set" and an
 * empty string as a value.
 */
export function buildCreatePersonBody(
  v: PersonDetailsValue,
  opts: { scoped: boolean; target: AllocationTarget },
): Record<string, unknown> {
  return {
    first_name: v.firstName.trim(),
    last_name: v.lastName.trim(),
    email: v.email.trim(),
    // A scoped session inherits the header switcher's mobilisation; the
    // all-mobilisations view must state a destination explicitly.
    ...(opts.scoped ? {} : { mobilisation_id: opts.target ?? null }),
    ...(v.personalEmail.trim() ? { personal_email: v.personalEmail.trim() } : {}),
    ...(v.employeeId.trim() ? { employee_id: v.employeeId.trim() } : {}),
    ...(v.roleId ? { role_id: v.roleId } : {}),
    ...(v.manager.trim() ? { manager: v.manager.trim() } : {}),
    ...(v.contract.trim() ? { contract: v.contract.trim() } : {}),
    ...(v.startDate.trim() ? { start_date: v.startDate.trim() } : {}),
    ...(v.notes.trim() ? { notes: v.notes.trim() } : {}),
    ...(v.onboardingHidden ? { onboarding_hidden: true } : {}),
  };
}
